import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import 'vis-timeline/styles/vis-timeline-graph2d.css';
import './scribane-timeline.css';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';

export interface ScribaneTimelineChapter {
  id?: string;
  title: string;
  order?: number;
  summary?: string;
}

interface ScribaneTimelineProps {
  chapters: ScribaneTimelineChapter[];
  className?: string;
  style?: React.CSSProperties;
  groupBy?: 'none' | 'character' | 'storyline' | 'scene';
  groupByCharacters?: Set<string>;
  groupByStorylines?: Set<string>;
  groupByScenes?: Set<string>;
  entityRefs?: Array<{ sourceType: string; sourceId: string; targetType: string; targetId: string }>;
  characters?: Array<{ id: string; name: string }>;
  storylines?: Array<{ id: string; title: string }>;
  scenes?: Array<{ id: string; title: string }>;
  onChapterClick?: (chapter: ScribaneTimelineChapter) => void;
}

function orderToDate(order: number): Date {
  return new Date(2000, 0, Math.max(1, order));
}

function createChapterItemTemplate(
  item: { order?: number; title?: string; excerpt?: string }
): string {
  const title = `${item.order ?? ''}. ${item.title ?? ''}`.trim();
  const excerptContent = item.excerpt
    ? escapeHtml(item.excerpt).replace(/\n/g, '<br>')
    : '';
  const excerptHtml = item.excerpt
    ? `<div class="scribane-timeline-chapter-item__excerpt">${excerptContent}</div>`
    : '';
  return `<div class="scribane-timeline-chapter-item"><div class="scribane-timeline-chapter-item__title"><strong>${escapeHtml(title)}</strong></div>${excerptHtml}</div>`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function extractCharacterIdsFromHtml(html: string): string[] {
  const ids: string[] = [];
  const regex = /<[^>]*data-entity-type="character"[^>]*>/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const idMatch = match[0].match(/data-id="([^"]*)"/);
    if (idMatch && !ids.includes(idMatch[1])) {
      ids.push(idMatch[1]);
    }
  }
  return ids;
}

function getLinesWithCharacterIds(html: string): Array<{ lineText: string; characterIds: string[] }> {
  const segments = html.trim().split(/(?:<\/p>|<\/div>|<br\s*\/?>)/gi);
  const result: Array<{ lineText: string; characterIds: string[] }> = [];
  for (const segment of segments) {
    const lineText = segment
      .replace(/<[^>]*>/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .trim();
    if (lineText.length === 0) continue;
    const characterIds = extractCharacterIdsFromHtml(segment);
    result.push({ lineText, characterIds });
  }
  return result;
}

export const ScribaneTimeline: React.FC<ScribaneTimelineProps> = ({
  chapters,
  className,
  style,
  groupBy = 'none',
  groupByCharacters = new Set(),
  groupByStorylines = new Set(),
  groupByScenes = new Set(),
  entityRefs = [],
  characters = [],
  storylines = [],
  scenes = [],
  onChapterClick,
}) => {
  const { t } = useLocale();
  const [hoveredItem, setHoveredItem] = useState<{
    itemId: string;
    position: { top: number; left: number };
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<{ destroy: () => void; setOptions: (opts: { height?: string }) => void } | null>(null);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [chapters]
  );

  const { visGroups, visItems } = useMemo(() => {
    const stripHtmlPreservingNewlines = (html: string): string =>
      html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n+/g, '\n')
        .trim();
    const truncateExcerpt = (s: string, max: number) =>
      s.length <= max ? s : s.slice(0, max - 1).trim() + '…';
    const MAX_EXCERPT = 200;

    const baseItems = sortedChapters.map((ch, index) => {
      const order = ch.order ?? index + 1;
      const excerpt = ch.summary?.trim()
        ? truncateExcerpt(stripHtmlPreservingNewlines(ch.summary), MAX_EXCERPT)
        : '';
      return {
        id: ch.id || `chapter-${index}`,
        content: '',
        start: orderToDate(order),
        end: orderToDate(order + 1),
        order,
        title: ch.title,
        excerpt: excerpt || undefined,
        style: 'min-height: 88px; height: 88px;',
      };
    });

    if (groupBy === 'storyline' && storylines.length > 0) {
      const chapterIds = new Set(sortedChapters.map((c) => c.id).filter(Boolean) as string[]);
      const chapterToStorylines = new Map<string, string[]>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'manual_chapter' && ref.targetType === 'storyline' && chapterIds.has(ref.sourceId)) {
          const list = chapterToStorylines.get(ref.sourceId) ?? [];
          if (!list.includes(ref.targetId)) list.push(ref.targetId);
          chapterToStorylines.set(ref.sourceId, list);
        }
      }
      const storylinesToShow =
        groupByStorylines.size > 0
          ? storylines.filter((s) => groupByStorylines.has(s.id))
          : storylines.filter((s) =>
              sortedChapters.some(
                (ch) => (chapterToStorylines.get(ch.id ?? '') ?? []).includes(s.id)
              )
            );
      const groups: { id: string; content: string }[] = storylinesToShow.map((s) => ({
        id: `storyline:${s.id}`,
        content: s.title,
      }));
      const ungroupedId = 'storyline:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items: Array<{
        id: string;
        content: string;
        start: Date;
        end: Date;
        order: number;
        title: string;
        excerpt?: string;
        group: string;
        style?: string;
      }> = [];
      for (const item of baseItems) {
        const storylineIds = chapterToStorylines.get(item.id) ?? [];
        const filteredStorylineIds =
          groupByStorylines.size > 0
            ? storylineIds.filter((id) => groupByStorylines.has(id))
            : storylineIds;
        const storylineIdsInGroups = filteredStorylineIds.filter((id) =>
          storylinesToShow.some((s) => s.id === id)
        );
        if (storylineIdsInGroups.length === 0) {
          items.push({
            ...item,
            group: ungroupedId,
            style: 'min-height: 88px; height: 88px;',
          });
        } else {
          for (const storylineId of storylineIdsInGroups) {
            items.push({
              ...item,
              id: `${item.id}__storyline__${storylineId}`,
              group: `storyline:${storylineId}`,
              style: 'min-height: 88px; height: 88px;',
            });
          }
        }
      }
      return { visGroups: groups, visItems: items };
    }

    if (groupBy === 'scene' && scenes.length > 0) {
      const chapterIds = new Set(sortedChapters.map((c) => c.id).filter(Boolean) as string[]);
      const chapterToScenes = new Map<string, string[]>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'manual_chapter' && ref.targetType === 'scene' && chapterIds.has(ref.sourceId)) {
          const list = chapterToScenes.get(ref.sourceId) ?? [];
          if (!list.includes(ref.targetId)) list.push(ref.targetId);
          chapterToScenes.set(ref.sourceId, list);
        }
      }
      const scenesToShow =
        groupByScenes.size > 0
          ? scenes.filter((s) => groupByScenes.has(s.id))
          : scenes.filter((s) =>
              sortedChapters.some(
                (ch) => (chapterToScenes.get(ch.id ?? '') ?? []).includes(s.id)
              )
            );
      const groups: { id: string; content: string }[] = scenesToShow.map((s) => ({
        id: `scene:${s.id}`,
        content: s.title,
      }));
      const ungroupedId = 'scene:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items: Array<{
        id: string;
        content: string;
        start: Date;
        end: Date;
        order: number;
        title: string;
        excerpt?: string;
        group: string;
        style?: string;
      }> = [];
      for (const item of baseItems) {
        const sceneIds = chapterToScenes.get(item.id) ?? [];
        const filteredSceneIds =
          groupByScenes.size > 0
            ? sceneIds.filter((id) => groupByScenes.has(id))
            : sceneIds;
        const sceneIdsInGroups = filteredSceneIds.filter((id) =>
          scenesToShow.some((s) => s.id === id)
        );
        if (sceneIdsInGroups.length === 0) {
          items.push({
            ...item,
            group: ungroupedId,
            style: 'min-height: 88px; height: 88px;',
          });
        } else {
          for (const sceneId of sceneIdsInGroups) {
            items.push({
              ...item,
              id: `${item.id}__scene__${sceneId}`,
              group: `scene:${sceneId}`,
              style: 'min-height: 88px; height: 88px;',
            });
          }
        }
      }
      return { visGroups: groups, visItems: items };
    }

    if (groupBy !== 'character' || characters.length === 0) {
      return {
        visGroups: [] as { id: string; content: string }[],
        visItems: baseItems.map((item) => ({
          id: item.id,
          content: item.content,
          start: item.start,
          end: item.end,
          order: item.order,
          title: item.title,
          excerpt: item.excerpt,
          style: 'min-height: 88px; height: 88px;',
        })),
      };
    }

    const chapterIds = new Set(sortedChapters.map((c) => c.id).filter(Boolean) as string[]);
    const chapterToCharacters = new Map<string, string[]>();
    for (const ref of entityRefs) {
      if (ref.sourceType === 'manual_chapter' && ref.targetType === 'character' && chapterIds.has(ref.sourceId)) {
        const chars = chapterToCharacters.get(ref.sourceId) ?? [];
        if (!chars.includes(ref.targetId)) chars.push(ref.targetId);
        chapterToCharacters.set(ref.sourceId, chars);
      }
    }

    const charsToShow =
      groupByCharacters.size > 0
        ? characters.filter((c) => groupByCharacters.has(c.id))
        : characters.filter((c) =>
            sortedChapters.some(
              (ch) => (chapterToCharacters.get(ch.id ?? '') ?? []).includes(c.id)
            )
          );

    const groups: { id: string; content: string }[] = charsToShow.map((c) => ({
      id: `character:${c.id}`,
      content: c.name,
    }));
    const ungroupedId = 'character:__ungrouped__';
    groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });

    const items: Array<{
      id: string;
      content: string;
      start: Date;
      end: Date;
      order: number;
      title: string;
      excerpt?: string;
      group: string;
      style?: string;
    }> = [];

    for (const item of baseItems) {
      const chapter = sortedChapters.find((ch, i) => (ch.id || `chapter-${i}`) === item.id);
      const linesWithChars = chapter?.summary?.trim()
        ? getLinesWithCharacterIds(chapter.summary)
        : [];
      const charIds = chapterToCharacters.get(item.id) ?? [];
      const filteredCharIds =
        groupByCharacters.size > 0
          ? charIds.filter((id) => groupByCharacters.has(id))
          : charIds;

      const charIdsInGroups = filteredCharIds.filter((id) =>
        charsToShow.some((c) => c.id === id)
      );
      if (charIdsInGroups.length === 0) {
        items.push({
          ...item,
          group: ungroupedId,
          style: 'min-height: 88px; height: 88px;',
        });
      } else {
        for (const charId of charIdsInGroups) {
          const linesForCharacter = linesWithChars.filter((line) =>
            line.characterIds.includes(charId)
          );
          const excerptForCharacter =
            linesForCharacter.length > 0
              ? truncateExcerpt(
                  linesForCharacter.map((l) => l.lineText).join('\n'),
                  MAX_EXCERPT
                )
              : undefined;
          items.push({
            ...item,
            id: `${item.id}__${charId}`,
            group: `character:${charId}`,
            excerpt: excerptForCharacter,
            style: 'min-height: 88px; height: 88px;',
          });
        }
      }
    }

    return { visGroups: groups, visItems: items };
  }, [sortedChapters, groupBy, groupByCharacters, groupByStorylines, groupByScenes, entityRefs, characters, storylines, scenes, t]);

  const getTimelineHeightPx = useCallback((): string => {
    return `${Math.round(window.innerHeight * 0.5)}px`;
  }, []);

  const resolveItemId = useCallback(
    (visItemId: string): string => {
      const baseId = visItemId.includes('__') ? visItemId.split('__')[0] : visItemId;
      const exact = sortedChapters.find((ch, i) => (ch.id || `chapter-${i}`) === baseId);
      return exact ? (exact.id ?? `chapter-${sortedChapters.indexOf(exact)}`) : baseId;
    },
    [sortedChapters]
  );

  useEffect(() => {
    if (sortedChapters.length === 0) {
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    let cancelled = false;
    const useGroups = visGroups.length > 0;
    const orders = sortedChapters.map((ch, i) => ch.order ?? i + 1);
    const minOrder = Math.min(...orders);
    const maxOrder = Math.max(...orders);
    const numChapters = sortedChapters.length;
    const visibleRangeDays = Math.min(4, Math.max(2, numChapters));
    const windowStart = orderToDate(minOrder);
    const windowEnd = orderToDate(minOrder + visibleRangeDays);
    const initTimeline = (): void => {
      if (cancelled || !containerRef.current) {
        return;
      }
      const el = containerRef.current;
      const tryCreate = (): void => {
        if (cancelled || !containerRef.current) return;
        if (el.offsetWidth === 0 || el.offsetHeight === 0) return;
        const observer = resizeObserverRef.current;
        if (observer) {
          observer.disconnect();
          resizeObserverRef.current = null;
        }
        import('vis-timeline/standalone').then(({ Timeline: TimelineClass }) => {
          if (cancelled || !containerRef.current) return;
          const fullMinDate = orderToDate(minOrder - 1);
          const fullMaxDate = orderToDate(maxOrder + 2);
          const options = {
            margin: { item: { horizontal: 10, vertical: 6 } },
            showTooltips: false,
            showMinorLabels: true,
            showMajorLabels: false,
            height: getTimelineHeightPx(),
            min: fullMinDate,
            max: fullMaxDate,
            template: (itemData: { order?: number; title?: string; excerpt?: string }) =>
              createChapterItemTemplate(itemData),
            format: {
              minorLabels: (date: Date) => String(new Date(date).getDate()),
            },
            ...(useGroups ? { groupHeightMode: 'fitItems' as const } : {}),
          };
          const timeline = useGroups
            ? new TimelineClass(el, visItems, visGroups, options)
            : new TimelineClass(el, visItems, options);
          const updateHeight = (): void => {
            if (timelineRef.current) {
              timelineRef.current.setOptions({ height: getTimelineHeightPx() });
            }
          };
          resizeHandlerRef.current = updateHeight;
          window.addEventListener('resize', updateHeight);
          timeline.setWindow(windowStart, windowEnd);
          timeline.on('itemover', (props: { item: string; event: MouseEvent }) => {
            if (hoverCloseTimeoutRef.current) {
              clearTimeout(hoverCloseTimeoutRef.current);
              hoverCloseTimeoutRef.current = null;
            }
            setHoveredItem({
              itemId: props.item,
              position: { top: props.event.clientY, left: props.event.clientX },
            });
          });
          timeline.on('itemout', () => {
            hoverCloseTimeoutRef.current = setTimeout(() => {
              setHoveredItem(null);
              hoverCloseTimeoutRef.current = null;
            }, 150);
          });
          if (onChapterClick) {
            timeline.on('click', (props: { item: string }) => {
              const chapterId = resolveItemId(props.item);
              const chapter = sortedChapters.find(
                (ch, i) => (ch.id || `chapter-${i}`) === chapterId
              );
              if (chapter) {
                onChapterClick(chapter);
              }
            });
          }
          timelineRef.current = timeline;
        });
      };
      if (el.offsetWidth > 0 && el.offsetHeight > 0) {
        tryCreate();
        return;
      }
      const observer = new ResizeObserver(() => {
        if (cancelled) return;
        tryCreate();
      });
      resizeObserverRef.current = observer;
      observer.observe(el);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(initTimeline);
    });
    return () => {
      cancelled = true;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = null;
      }
      setHoveredItem(null);
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
    };
  }, [sortedChapters, visItems, visGroups, getTimelineHeightPx, resolveItemId, onChapterClick]);

  if (sortedChapters.length === 0) {
    return (
      <div className={`scribane-timeline ${className ?? ''}`.trim()} style={{ ...style, minHeight: 400, textAlign: 'center', padding: '3rem', color: '#6b7280', fontFamily: 'var(--font-body)', fontSize: '14px' }}>
        <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{t('no_chapters')}</p>
        <p style={{ fontSize: '0.875rem' }}>{t('add_headings_for_chapters')}</p>
      </div>
    );
  }

  return (
    <div className={`scribane-timeline scribane-timeline--chapters ${className ?? ''}`.trim()} style={{ ...style, minHeight: 400 }}>
      <div ref={containerRef} style={{ flex: 1, minHeight: 400, maxHeight: '50vh' }} />
      {hoveredItem &&
        (() => {
          const chapter = sortedChapters.find(
            (ch, i) => (ch.id || `chapter-${i}`) === resolveItemId(hoveredItem.itemId)
          );
          if (!chapter) return null;
          const order = chapter.order ?? sortedChapters.indexOf(chapter) + 1;
          return (
            <HoverCard open={true}>
              <HoverCardTrigger asChild>
                <div
                  style={{
                    position: 'fixed',
                    top: hoveredItem.position.top,
                    left: hoveredItem.position.left,
                    width: 1,
                    height: 1,
                    pointerEvents: 'none',
                    opacity: 0,
                    zIndex: -1,
                  }}
                />
              </HoverCardTrigger>
              <HoverCardContent
                className="max-w-2xl w-auto px-4 py-2"
                side="bottom"
                align="center"
                sideOffset={8}
                collisionPadding={8}
                onPointerEnter={() => {
                  if (hoverCloseTimeoutRef.current) {
                    clearTimeout(hoverCloseTimeoutRef.current);
                    hoverCloseTimeoutRef.current = null;
                  }
                }}
                onPointerLeave={() => {
                  hoverCloseTimeoutRef.current = setTimeout(() => {
                    setHoveredItem(null);
                    hoverCloseTimeoutRef.current = null;
                  }, 150);
                }}
              >
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground">
                    {Number.isInteger(order) ? t('chapter_number') : t('chapter_order')}: {order}
                  </p>
                  <p className="font-semibold leading-tight">{chapter.title}</p>
                  {chapter.summary?.trim() ? (
                    <div
                      className={`${RICH_TEXT_HTML_CLASS} text-muted-foreground [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0`}
                      dangerouslySetInnerHTML={{ __html: chapter.summary.trim() }}
                    />
                  ) : null}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        })()}
    </div>
  );
};
